/**
 * Shared Components Exports
 *
 * This file re-exports shared UI components for easy import across the project:
 * - MainCard: Flexible card layout with header, subtitle, filter, and secondary actions
 * - SubCard: Simple card layout with header and secondary actions
 * - Modal: Reusable modal dialog with portal, title, close button, and custom content
 * - MainTable: Table component with sortable columns, pagination, and custom cell rendering
 * - Pagination: Pagination controls for navigating large datasets
 * - CheckBox: Custom checkbox and radio button components with labels
 *
 * Each component is designed for flexibility and reusability across different parts of the application.
 */
export { default as Autocomplete } from "./CAutocomplete/CAutocomplete";
export { default as MainCard } from "./CCard/MainCard";
export { default as SubCard } from "./CCard/SubCard";
export { default as CheckBox } from "./CCheckRadio/CCheckRadio";
export { default as AreaChart } from "./Charts/AreaChart";
export { default as BarChart } from "./Charts/BarChart";
export { default as LineChart } from "./Charts/LineChart";
export { default as PieChart } from "./Charts/PieChart";
export { default as RadarChart } from "./Charts/RadarChart";
export { default as RadialChart } from "./Charts/RadialChart";
export { default as Input, default as CInput } from "./CInput/CInput";
// CList - Unified list component with variants (activity, avatar, status)
export {
  default as CList,
  ActivityListItem,
  AvatarListItem,
  StatusListItem,
  ACTIVITY_ICON_STYLES,
  AVATAR_BADGE_CONFIG,
  STATUS_CONFIG,
} from "./CActivityList";
export type {
  CListProps,
  ActivityListProps,
  AvatarListProps,
  StatusListProps,
  ActivityItemType,
  AvatarItemType,
  StatusItemType,
  ActivityBadgeVariant,
  AvatarBadgeVariant,
  StatusVariant,
} from "./CActivityList";
export { default as CModal } from "./CModal/CModal";
export { default as ConfirmationDialog } from "./ConfirmDialog/ConfirmationDialog";
export { default as Pagination } from "./CPagination/CPagination";
export { default as Select, default as CSelect } from "./CSelect/CSelect";
export { default as MainTable } from "./CTable/CTable";
export {
  default as TextArea,
  default as CTextArea,
} from "./CTextArea/CTextArea";
export { default as DynamicForm } from "./Form/DynamicForm";
export { default as SearchableSelect } from "./SearchableSelect/SearchableSelect";
export { default as SearchInput } from "./SearchInput/SearchInput";
export { default as StatusBadge } from "./StatusBadge/StatusBadge";
export { default as ThemeToggle } from "./ThemeToggle/ThemeToggle";
export { default as CustomAlert } from "./CustomAlert/CustomAlert";
export {
  Tabs,
  Tabs as CustomTabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "./CTabs/CTabs";
export { ChartWrapper } from "./Charts/ChartWrapper";
export { default as ErrorBoundary } from "./ErrorBoundary/ErrorBoundary";
export { default as FeatureErrorBoundary } from "./ErrorBoundary/FeatureErrorBoundary";
export { default as RouteErrorBoundary } from "./ErrorBoundary/RouteErrorBoundary";

// New components from staff-dashboard
export {
  default as CBadge,
  CBadge as Badge,
  badgeVariants,
} from "./CBadge/CBadge";
export type { CBadgeProps } from "./CBadge/CBadge";
export { default as CButton } from "./CButton/CButton";
export { CButton as SharedButton } from "./CButton/CButton";

// CKpiCard - KPI stat card with glassmorphism, trend indicators, accent colors
export { default as CKpiCard } from "./CKpiCard/CKpiCard";
export type {
  CKpiCardProps,
  CKpiCardBadge,
  CKpiCardTrend,
  AccentColor,
  BadgeVariant as KpiBadgeVariant,
} from "./CKpiCard/CKpiCard";
export {
  accentColorConfig,
  trendConfig,
  badgeVariantConfig,
} from "./CKpiCard/config";

// CButtonGroup - Button group for single value selection (e.g., Business Type)
export { default as CButtonGroup } from "./CButtonGroup/CButtonGroup";
export type {
  CButtonGroupProps,
  CButtonGroupOption,
} from "./CButtonGroup/CButtonGroup";

// CDropzone - File upload drag & drop component
export { default as CDropzone } from "./CDropzone/CDropzone";
export type { CDropzoneProps } from "./CDropzone/CDropzone";

// CProgressBar - Progress bar for showing completion percentage
export { default as CProgressBar } from "./CProgressBar/CProgressBar";
export type { CProgressBarProps } from "./CProgressBar/CProgressBar";

// CAvatar - Avatar component for initials or icons
export { default as CAvatar } from "./CAvatar/CAvatar";

// CDomainVerification - Domain verification card with DNS records table
export { default as CDomainVerification } from "./CDomainVerification/CDomainVerification";
export type { CDomainVerificationProps } from "./CDomainVerification/CDomainVerification";
export type {
  CAvatarProps,
  AvatarSize,
  AvatarColor,
  AvatarShape,
} from "./CAvatar/CAvatar";
